(() => {
  const MAX_FILE = 10 * 1024 * 1024;
  const ENDPOINT =
    /^https:\/\/script\.google\.com\/macros\/s\/[A-Za-z0-9_-]+\/exec$/;
  let captchaPromise;
  const loadCaptcha = (sitekey) =>
    (captchaPromise ||= new Promise((resolve, reject) => {
      const timeout = setTimeout(
        () =>
          reject(
            new Error("Security check could not load. Refresh and try again."),
          ),
        15000,
      );
      const ready = () => {
        if (!window.grecaptcha?.enterprise) return;
        window.grecaptcha.enterprise.ready(() => {
          clearTimeout(timeout);
          resolve(window.grecaptcha.enterprise);
        });
      };
      if (window.grecaptcha?.enterprise) return ready();
      const script = document.createElement("script");
      script.src =
        "https://www.google.com/recaptcha/enterprise.js?render=" +
        encodeURIComponent(sitekey);
      script.onload = ready;
      script.onerror = () => {
        clearTimeout(timeout);
        reject(
          new Error("Security check could not load. Refresh and try again."),
        );
      };
      document.head.append(script);
    }));
  const captchaToken = async (sitekey) => {
    const api = await loadCaptcha(sitekey);
    let timer;
    try {
      return await Promise.race([
        api.execute(sitekey, { action: "native_form" }),
        new Promise((_, reject) => {
          timer = setTimeout(
            () =>
              reject(new Error("Security check timed out. Please try again.")),
            15000,
          );
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  };
  const readFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(",")[1]);
      reader.onerror = () =>
        reject(
          new Error("The file could not be read. Please choose it again."),
        );
      reader.readAsDataURL(file);
    });
  const init = (form) => {
    if (form.dataset.nativeReady) return;
    form.dataset.nativeReady = "true";
    const schema = JSON.parse(
      form.querySelector("[data-native-schema]").textContent,
    );
    const preview = form.dataset.preview !== "false";
    const endpoint = form.dataset.endpoint || "";
    const button = form.querySelector("[data-native-submit]");
    const message = form.querySelector("[data-native-message]");
    let pending = false,
      attemptId = crypto.randomUUID();
    let transport, frame, timer;
    button.textContent = preview
      ? form.dataset.previewLabel || "Review answers"
      : "Submit";
    const show = (text, state = "error") => {
      message.textContent = text;
      message.dataset.state = state;
      message.hidden = false;
      message.focus({ preventScroll: true });
      message.scrollIntoView({ block: "nearest", behavior: "auto" });
    };
    const clearTransport = () => {
      clearTimeout(timer);
      transport?.remove();
      frame?.remove();
      transport = frame = null;
    };
    const unlock = () => {
      pending = false;
      button.disabled = false;
      form.removeAttribute("aria-busy");
      button.textContent = preview
        ? form.dataset.previewLabel || "Review answers"
        : "Submit";
    };
    if (!preview && (!ENDPOINT.test(endpoint) || !form.dataset.sitekey)) {
      button.disabled = true;
      show("This form is not available yet. Please contact hei@ochaya.no.");
    }
    form.querySelectorAll(".ochaya-native__help").forEach((help) => {
      const toggle = help.querySelector("button");
      const tooltip = help.querySelector('[role="tooltip"]');
      let pinned = false;
      const setOpen = (open) => {
        tooltip.hidden = !open;
        toggle.setAttribute("aria-expanded", String(open));
        if (open) {
          tooltip.style.transform = "none";
          const rect = tooltip.getBoundingClientRect();
          const shift = Math.max(
            12 - rect.left,
            Math.min(0, document.documentElement.clientWidth - 12 - rect.right),
          );
          tooltip.style.transform = `translateX(${shift}px)`;
        }
      };
      // Hover is supplemental; click/tap and keyboard work independently.
      help.addEventListener("pointerenter", (event) => {
        if (event.pointerType === "mouse") setOpen(true);
      });
      help.addEventListener("pointerleave", () => {
        if (!pinned) setOpen(false);
      });
      toggle.addEventListener("click", () => {
        pinned = !pinned;
        setOpen(pinned);
      });
      toggle.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          pinned = false;
          setOpen(false);
          event.stopPropagation();
        }
      });
      help.addEventListener("focusout", (event) => {
        if (!help.contains(event.relatedTarget)) {
          pinned = false;
          setOpen(false);
        }
      });
      form.addEventListener("pointerdown", (event) => {
        if (!help.contains(event.target)) {
          pinned = false;
          setOpen(false);
        }
      });
    });
    form.addEventListener("change", (event) => {
      const field = event.target;
      if (field.type === "file") {
        const file = field.files?.[0],
          question = schema.questions.find(
            (q) => String(q.entryId) === field.dataset.fileEntry,
          );
        const extension = file?.name.split(".").pop().toLowerCase();
        const allowed =
          question?.accept.split(",").map((v) => v.slice(1)) || [];
        const invalid =
          file && (file.size > MAX_FILE || !allowed.includes(extension));
        field.setCustomValidity(
          invalid ? "Choose an allowed file of 10 MB or less." : "",
        );
        field
          .closest(".ochaya-native__field")
          .querySelector("[data-file-status]").textContent = file
          ? invalid
            ? "Choose an allowed file of 10 MB or less."
            : `${file.name} · ${(file.size / 1024 / 1024).toFixed(1)} MB`
          : "";
      }
      if (field.type === "radio") {
        const entry = field.name.match(/\[(\d+)\]/)?.[1];
        const other = form.querySelector(`[data-other-for="${entry}"]`);
        if (other) {
          other.hidden = field.value !== "Other";
          other.disabled = other.hidden;
          other.required = !other.hidden;
        }
      }
    });
    const clearResolvedError = (event) => {
      const field = event.target;
      if (field.matches("input,select,textarea") && field.validity.valid)
        field.removeAttribute("aria-invalid");
    };
    form.addEventListener("input", clearResolvedError);
    form.addEventListener("change", clearResolvedError);
    window.addEventListener("message", (event) => {
      let origin;
      try {
        origin = new URL(event.origin);
      } catch {
        return;
      }
      // Apps Script responses run inside its isolated googleusercontent iframe.
      if (
        origin.protocol !== "https:" ||
        !(
          origin.hostname === "script.google.com" ||
          origin.hostname.endsWith(".googleusercontent.com")
        )
      )
        return;
      const data = event.data;
      if (
        !pending ||
        data?.type !== "ochaya-native-result" ||
        data.submissionId !== attemptId
      )
        return;
      clearTransport();
      unlock();
      if (data.ok) {
        show(
          `Thank you. Your ${schema.key === "events" ? "registration" : schema.key === "careers" ? "application" : "enquiry"} has been received. Reference: ${attemptId}`,
          "success",
        );
        button.disabled = true;
      } else
        show(
          data.message || "We could not save your answers. Please try again.",
        );
    });
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (pending || form.elements.website_confirmation.value) return;
      const invalid = [...form.querySelectorAll("input,select,textarea")].find(
        (e) => !e.checkValidity(),
      );
      if (invalid) {
        invalid.reportValidity();
        return;
      }
      const fields = new FormData(form),
        answers = {},
        files = {};
      for (const q of schema.questions) {
        const name = `answers[${q.entryId}]`;
        if (q.type === "file") continue;
        let value = String(fields.get(name) || "").trim();
        if (value === "Other")
          value = String(fields.get(`other[${q.entryId}]`) || "").trim();
        answers[q.entryId] = value;
      }
      if (preview) {
        form.querySelector("[data-native-review]")?.remove();
        const review = document.createElement("dl");
        review.className = "ochaya-native__review";
        review.dataset.nativeReview = "";
        for (const q of schema.questions) {
          const term = document.createElement("dt"),
            answer = document.createElement("dd");
          term.textContent = q.label;
          answer.textContent =
            q.type === "file"
              ? fields.get(`answers[${q.entryId}]`)?.name || "—"
              : form.querySelector(`select[name="answers[${q.entryId}]"]`)?.selectedOptions[0]?.textContent || answers[q.entryId] || "—";
          review.append(term, answer);
        }
        form.append(review);
        show(
          form.dataset.previewMessage ||
            "Preview only — nothing has been sent or saved. You can go back and edit your answers.",
          "preview",
        );
        return;
      }
      if (!ENDPOINT.test(endpoint) || !form.dataset.sitekey) {
        show("Complete the security check before submitting.");
        return;
      }
      pending = true;
      button.disabled = true;
      button.textContent = "Sending…";
      form.setAttribute("aria-busy", "true");
      try {
        let total = 0;
        for (const q of schema.questions.filter((q) => q.type === "file")) {
          const file = fields.get(`answers[${q.entryId}]`);
          if (!file?.size) continue;
          total += file.size;
          if (file.size > MAX_FILE || total > MAX_FILE * 2)
            throw Error(
              "Files are too large. Maximum 10 MB each, 20 MB in total.",
            );
          files[q.entryId] = {
            name: file.name,
            mimeType: file.type,
            data: await readFile(file),
          };
        }
        // Obtain a fresh, single-use token after file reading, immediately before POST.
        const token = await captchaToken(form.dataset.sitekey);
        if (typeof token !== "string" || !token)
          throw Error("Security check failed. Please try again.");
        const payload = {
          version: 1,
          formType: schema.key,
          submissionId: attemptId,
          origin: location.origin,
          locale: document.documentElement.lang,
          answers,
          files,
          captchaToken: token,
          website_confirmation: "",
        };
        frame = document.createElement("iframe");
        frame.name = `ochaya-native-${attemptId}`;
        frame.hidden = true;
        frame.title = "Secure form submission";
        transport = document.createElement("form");
        transport.method = "POST";
        transport.action = endpoint;
        transport.target = frame.name;
        transport.hidden = true;
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "payload";
        input.value = JSON.stringify(payload);
        transport.append(input);
        document.body.append(frame, transport);
        timer = setTimeout(() => {
          clearTransport();
          unlock();
          show(
            "Confirmation is taking longer than expected. Your answers may have been received. Retry with the same reference to avoid duplicates.",
          );
        }, 60000);
        transport.submit();
      } catch (error) {
        clearTransport();
        unlock();
        show(error.message);
      }
    });
  };
  const start = (root = document) =>
    root.querySelectorAll("[data-native-form]").forEach(init);
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", () => start(), {
      once: true,
    });
  else start();
  document.addEventListener("shopify:section:load", (event) =>
    start(event.target),
  );
})();
